import getHostname from './getHostname';

/**
 * @function isTrustedURL
 * Returns whether or not a given url is trusted given a list of whitelisted domains.
 *
 * Note: '.autodesk.com' is always trusted.
 *
 * @example
 * //returns true
 * isTrustedURL('http://www.google.com', /www.(google|youtube).com/);
 * @example
 * //returns false
 * isTrustedURL('http://www.youtube.com', 'www.google.com');
 * @example
 * //even though the first element in the whitelist results in false, the secound one is true so the url is trusted.
 * // returns true
 * isTrustedURL('http://he.wikipedia.org', [({ hostname }) => hostname.includes('en'), /\.wikipedia.org/]);
 *
 * @param {String} url - the URL to test if is trusted or not.
 * @param {String|RegExp|Function|Array<String|RegExp|Function>} whitelist - the whitelist of domains that are trusted. can be a string, regexp, function or an array of all of them.
 *
 * @returns {Boolean}
 */
export default (url, whitelist = []) => {
  const allowedHostnames = [/\.autodesk\.com/].concat(
    Array.isArray(whitelist) ? whitelist : [whitelist]
  );
  const getRegExpBody = regexp => {
    const regexpString = regexp.toString();
    const regexpEnd = regexpString.lastIndexOf('/');

    // This ignores the flags of the regexp (if there are ones) and fix the "\" so they're parsed correctly by the RegExp constructor.
    return regexpString.slice(1, regexpEnd).replace(/\\/g, '\\');
  };

  // To understand why we can't use `matcherObject.constructor.name` and have to define `canExecute` see this thread -> https://autodesk.slack.com/archives/CAH4M5A0M/p1548348519092600?thread_ts=1548238885.090100&cid=CAH4M5A0M
  // All the matchers are that the given allowed host name is found at the end of the hostname in the given url.
  const matchers = [
    {
      canExecute: matcherObject =>
        (typeof matcherObject).toLowerCase() === 'string' || matcherObject instanceof String,

      // The allowed hostname is specified as string so matching as with indices.
      execute: (allowedHostname, hostname) =>
        hostname.lastIndexOf(allowedHostname) === hostname.length - 1 - (allowedHostname.length - 1)
    },
    {
      canExecute: matcherObject => matcherObject instanceof RegExp,

      // The allowed hostname is specified as a RegExp so matching with "$" and "g" to make sure the RegExp is found at the end of the string (even if there are multiple matches).
      execute: (allowedHostname, hostname) =>
        new RegExp(`${getRegExpBody(allowedHostname)}$`, 'ig').test(hostname)
    },
    {
      canExecute: matcherObject => matcherObject instanceof Function,

      // The allowed hostname is specified as a function so it's up to the consumer to make sure the given hostname is as it should be, we coerce the result to a boolean at the end.
      execute: (allowedHostname, hostname) => !!allowedHostname({ hostname })
    }
  ];

  const matchHostname = hostname =>
    allowedHostnames.some(allowedHostname => {
      const matcher = matchers.filter(matcher => matcher.canExecute(allowedHostname))[0];
      return matcher.execute(allowedHostname, hostname);
    });

  try {
    return matchHostname(getHostname(url));
  } catch (e) {
    // In case a malformed url was sent the URL parsers would throw.
    // In this case just return false, it's definitely not an autodesk URL.
    return false;
  }
};
